// ── law_focus_pull — netti goes out of focus, it is never frosted over ──────
//
// netti used to step back behind words under a FROST: a copy of it, blurred by
// a backdrop-filter inside a radial mask, cross-faded over the sharp creature by
// the reading ramp. Half-way through that fade you see both at once, the sharp
// petals and a blurred ghost of them, which reads as a wet, gritty smear; and
// the mask's ellipse left the petal tips outside it perfectly sharp while the
// middle was soft, so the blur seemed to come from several directions.
//
// A camera does it differently, and so do we now: the creature itself is
// defocused, uniformly, by ONE `filter: blur()` on its own layer (see
// .ob-focus in components/organism-focus.css). One value, 0..1, says how far
// out of focus it is:
//
//   · the SCROLL ramp takes it out quickly: fully soft a little past half of a
//     surface's own ramp (FOCUS_REACH), and it simply stays there however far
//     you read; scrolling back up brings it in over the same short stretch, and
//     at the top it is sharp again.
//   · a FLOOR says how far out of focus a place is before anyone scrolls: the
//     navigator's outer rooms (your projects, a project's own page) keep netti
//     sharp; deeper in (a folder, a document, settings) it rests in the
//     background already, where it costs least (the calm bus drops its frame
//     rate and resolution, which a defocused creature never shows).
//
// The value is quantised (FOCUS_STEPS) and eased by one short CSS transition,
// so a scroll writes at most ~10 distinct radii across the whole pull and the
// compositor animates between them; nothing here runs per frame.

export const FOCUS_REACH = 0.62;
export const FOCUS_STEPS = 10;

// ── THE READING POSE (2026-10-22, John) ─────────────────────────────────────
// "When netti is blurred and in reader mode, it should feel bigger and more
// beautiful … some very big thing subtly floating away at the back." Out of
// focus used to mean only softer and fainter, which left a blurred blob of
// colour. Now a page that is READ (fully out of focus: a document, a page whose
// words run past the fold, a scroll well into anything) also takes the reading
// pose: netti grows and settles lower, behind the words (organism-focus.css),
// and draws itself out of focus with its own lens instead of being blurred
// afterwards (FutureOrganism.setDefocus): every petal, vein and node cell
// still there, soft, at a fraction of the pixels.
//
// The pose COMMITS (0 or 1, like the fold): it moves once, slowly, when a page
// becomes a reading page, never per scroll frame. A deep folder rests netti
// soft (DEEP_FOCUS) without posing it: a grid of tiles is not being read.

/** the window event the lens listens to: `{ focus, read }` (both 0..1). */
export const FOCUS_EVENT = "innernet:netti-focus";

const ramps: Record<string, number> = {};
// each place that says how far out of focus it rests: the dashboard's depth, a
// reading page's (setReadingFloor). The deepest one wins.
const floors: Record<string, number> = {};
let written = -1;
let posed = -1;

/** 0..1 → the quantised focus a ramp asks for (fully soft at FOCUS_REACH). */
export function focusFromRamp(ramp: number): number {
  const f = Math.max(0, Math.min(1, ramp / FOCUS_REACH));
  return Math.round(f * FOCUS_STEPS) / FOCUS_STEPS;
}

/** the reading pose: on only when something says the page is fully read (a
 *  floor of 1, or a scroll that took netti all the way out of focus). */
export function readingPose(floorValues: number[], rampValues: number[]): 0 | 1 {
  for (const v of floorValues) if (v >= 1) return 1;
  for (const v of rampValues) if (v >= 1) return 1;
  return 0;
}

function write() {
  if (typeof document === "undefined") return;
  let f = 0;
  for (const k in floors) f = Math.max(f, floors[k]);
  for (const k in ramps) f = Math.max(f, ramps[k]);
  f = Math.round(Math.max(0, Math.min(1, f)) * FOCUS_STEPS) / FOCUS_STEPS;
  const read = readingPose(Object.values(floors), Object.values(ramps));
  if (f === written && read === posed) return;
  written = f;
  posed = read;
  const root = document.documentElement;
  root.style.setProperty("--netti-focus", String(f));
  root.style.setProperty("--netti-read", String(read));
  // the filter exists only while netti is out of focus: a blur(0) is still an
  // offscreen pass, so a sharp netti carries no filter at all.
  root.toggleAttribute("data-netti-focus", f > 0);
  root.toggleAttribute("data-netti-read", read === 1);
  try {
    window.dispatchEvent(new CustomEvent(FOCUS_EVENT, { detail: { focus: f, read } }));
  } catch {
    /* a listener's error never breaks a scroll frame */
  }
}

/** where the focus stands now, for a netti mounting mid-read. */
export function currentNettiFocus(): { focus: number; read: number } {
  return { focus: Math.max(0, written), read: Math.max(0, posed) };
}

/** one surface's scroll ramp (0..1 over that surface's own reach). */
export function setFocusRamp(prefix: string, ramp: number): void {
  ramps[prefix] = focusFromRamp(ramp);
  write();
}

export function clearFocusRamp(prefix: string): void {
  delete ramps[prefix];
  write();
}

/** how far out of focus this place is before any scroll (0 sharp · 1 soft).
 *  `key` names who says so (the dashboard's shell by default); 0 lets it go. */
export function setFocusFloor(v: number, key = "shell"): void {
  const f = Math.max(0, Math.min(1, v));
  if (f > 0) floors[key] = f;
  else delete floors[key];
  write();
}

// ── law_focus_pull — revision 2026-10-19 (John): a page that is READ rests netti
// out of focus from the moment it opens. "When the page has enough scrollable
// content that goes beyond the visible fold, or any time in a reader view,
// especially on mobile, the netti needs to be blurred and darkened so that the
// text is clearly visible as soon as you get on this page." A reading surface
// passes whether its words run past the fold (or that it is a document, which
// always counts); the creature is then fully soft before anyone scrolls, and it
// comes back into focus only where a page fits on one screen.

/** how much of a screen a page must run past the fold before it counts as read
 *  (a sliver of overflow, a footer's worth, is not reading) */
export const READ_OVERFLOW = 0.18;

/** does a page this tall, in a window this tall, read past its fold? */
export function readsPastFold(scrollHeight: number, clientHeight: number): boolean {
  if (clientHeight <= 0) return false;
  return scrollHeight - clientHeight > clientHeight * READ_OVERFLOW;
}

/** a reading surface's floor: fully out of focus while `reading`, or let go. */
export function setReadingFloor(key: string, reading: boolean): void {
  setFocusFloor(reading ? 1 : 0, `read:${key}`);
}

/** the level netti should rest at for what it is showing (the calm bus). */
export function currentFocus(): number {
  return Math.max(0, written);
}
